import { Button, Flex, Group, Menu, Space, Stack, Text } from "@mantine/core";
import { type JSX, useState } from "react";
import { withPolymorphicClickAnalytics } from "@/analytics";
import { ActivityBubble, CaretDown, CaretUp } from "@/common-ui";
import { usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import type { AgentActivity } from "@/sdk";
import { useAgentActivity, useAgentSdk, useAgentStore, useCallbackState } from "@/sdk";
import { useStopwatch } from "@/shared-utils";
import classes from "./AgentStatus.module.css";

const AnalyticMenuItem = withPolymorphicClickAnalytics(Menu.Item<"button">, "Click");
const AnalyticsButton = withPolymorphicClickAnalytics(Button<"button">, "Click");

function ActivityClock({ activityUpdatedAt }: { activityUpdatedAt: number }) {
    const activityClockString = useStopwatch(activityUpdatedAt);
    return (
        <Text ff="mono" size="xs" className={classes.activityClock}>
            ({activityClockString})
        </Text>
    );
}

export function AgentStatus(): JSX.Element {
    const agentSdk = useAgentSdk();
    const { activity: currentActivity, pendingActivity, activityUpdatedAt } = useAgentStore();
    const { loadingSetAgentActivity, setNewAgentActivity } = useAgentActivity();
    const shouldShowClock = !!activityUpdatedAt;
    const [opened, setOpened] = useState(false);
    const { disableAgentStatusChange } = usePartnerFeatureConfigured();
    const callbackState = useCallbackState();

    if (!currentActivity) throw new Error("agent activity not yet initialized");

    /** Allow agents to set pending status, anything but Available is valid when in unselectable activity */
    const isDisabled = (activity: AgentActivity) => {
        if (activity === currentActivity) return true;
        const hasCallback = callbackState && callbackState.callbackDelay && callbackState.scheduledFor;
        return (
            !agentSdk.isAgentActivitySelectable(currentActivity) &&
            activity === agentSdk.availableAgentActivity &&
            currentActivity !== agentSdk.defaultAgentActivity &&
            !hasCallback
        );
    };

    const menuActivities = agentSdk.agentActivities.filter((activityName) =>
        agentSdk.isAgentActivitySelectable(activityName),
    );

    function getActivityBubbleVariant(activity: AgentActivity) {
        if (activity === agentSdk.availableAgentActivity) return "available";
        if (activity === agentSdk.offlineAgentActivity) return "offline";
        return "custom";
    }

    const menuItems = menuActivities.map((activity) => (
        <AnalyticMenuItem
            analytics={{
                actionId: "agent-activity-item",
                eventData: {
                    activity: activity,
                },
            }}
            id={`agent-activity-item-${activity}`}
            data-testid={`agent-activity-item-${activity}`}
            disabled={isDisabled(activity)}
            key={activity}
            leftSection={<ActivityBubble variant={getActivityBubbleVariant(activity)} />}
            onClick={() => void setNewAgentActivity(activity)}
        >
            <Text ml="-5" size="xs">
                {activity}
            </Text>
        </AnalyticMenuItem>
    ));

    const activityBubbleVariant = getActivityBubbleVariant(currentActivity);

    return (
        <>
            {disableAgentStatusChange ? (
                <Stack gap="2px" id="agent-activity-disabled" ml="xs">
                    <Group justify="center" gap="0" wrap="nowrap">
                        <ActivityBubble variant={activityBubbleVariant} />
                        <Space w="5" />
                        <Text size="xs">{currentActivity}</Text>
                        <Space w="4px" />
                        {shouldShowClock ? <ActivityClock activityUpdatedAt={activityUpdatedAt} /> : null}
                        <Space w="xs" />
                    </Group>
                    {pendingActivity ? (
                        <Flex justify="center">
                            <Text c="gray" size="xxs">
                                Pending:
                            </Text>
                            <ActivityBubble variant={activityBubbleVariant} size={10} />
                            <Text size="xxs">{pendingActivity}</Text>
                        </Flex>
                    ) : null}
                </Stack>
            ) : (
                <Menu
                    shadow="sm"
                    zIndex="var(--mantine-priority-highest)"
                    opened={opened}
                    radius="md"
                    closeOnClickOutside
                    onChange={setOpened}
                >
                    <Menu.Target>
                        <AnalyticsButton
                            analytics={{
                                actionId: "AgentStatus_ActivityDropdown",
                            }}
                            color="var(--mantine-color-text)"
                            id="agent-activity-button"
                            leftSection={<ActivityBubble variant={activityBubbleVariant} />}
                            data-testid="agent-activity"
                            disabled={loadingSetAgentActivity || disableAgentStatusChange}
                            rightSection={opened ? <CaretUp /> : <CaretDown />}
                            className={classes.agentStatusButton}
                            data-opened={opened}
                            size="xs"
                            variant="subtle"
                        >
                            <Stack gap="2px">
                                <Group justify="center" gap="0" wrap="nowrap">
                                    <Text size="xs">{currentActivity}</Text>
                                    <Space w="4px" />
                                    {shouldShowClock ? <ActivityClock activityUpdatedAt={activityUpdatedAt} /> : null}
                                </Group>
                                {pendingActivity ? (
                                    <Flex justify="center">
                                        <Text c="gray" size="xxs">
                                            Pending:
                                        </Text>
                                        <ActivityBubble variant={getActivityBubbleVariant(pendingActivity)} size={10} />
                                        <Text size="xxs">{pendingActivity}</Text>
                                    </Flex>
                                ) : null}
                            </Stack>
                        </AnalyticsButton>
                    </Menu.Target>
                    <Menu.Dropdown>{menuItems}</Menu.Dropdown>
                </Menu>
            )}
        </>
    );
}
